import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { lgGreen, lgControlHover } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02] the opt-in liquid look

/* Full-width strip that holds the search field above each page's toolbar. */
const Container = styled.div`
  background: ${(p) => p.theme.surface};
  border-bottom: 1px solid ${(p) => p.theme.line};
  padding: 12px 20px;
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1), border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 600px) {
    padding: 10px 12px;
  }
`;

/* Prominent rounded search field — matches the User Management search box
   (bordered, filled, green focus ring) so it stands out from the page.
   In `$inline` mode it drops the full-width strip and grows to fill the middle
   of a toolbar (used on the Payments page between the title and the filters). */
const Field = styled.div<{ $inline?: boolean; $liquid?: boolean }>`
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 14px;
  ${(p) => (p.$inline ? 'flex: 1; min-width: 160px; margin: 0 16px;' : '')}
  border-radius: 10px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.65)')};
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:focus-within {
    border-color: ${(p) => p.theme.green600};
    box-shadow: 0 0 0 3px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)')};
  }

  /* On phones the toolbar stacks vertically, so the inline search drops its side
     margins and fills the full row (the stacked toolbar provides the spacing). */
  @media (max-width: 768px) {
    ${(p) => (p.$inline ? 'margin: 0; min-width: 0;' : '')}
  }

  /* [LIQUID-GLASS 2026-11-02, user] OPT-IN (prop liquid, the Payments toolbar): a capsule pressed into the glass, like the
     glass filter buttons beside it (34 px, their height); typing gives it a soft green ring and glow. Phones keep 38 px. */
  ${(p) => p.$liquid && css`
    height: 34px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: ${p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)'};
    box-shadow: ${p.theme.colorScheme === 'dark'
      ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
      : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)'};
    &:focus-within {
      border-color: color-mix(in srgb, ${lgGreen(p)} 70%, transparent);
      box-shadow: ${p.theme.colorScheme === 'dark'
        ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
        : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)'},
        0 0 0 4px color-mix(in srgb, ${lgGreen(p)} 16%, transparent),
        0 0 18px color-mix(in srgb, ${lgGreen(p)} 24%, transparent);
    }
    @media (max-width: 768px) {
      height: 38px;
    }
  `}
`;

const Icon = styled.div`
  color: ${(p) => p.theme.muted};
  font-size: 13px;
  margin-right: 10px;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.text};

  &::placeholder {
    /* [FULL-INK 2026-09-01] theme.dim, not muted — a full-ink placeholder reads as typed text. */
    color: ${(p) => p.theme.dim};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: the field can be narrower than an input's built-in width (a search sharing its
     row with the record count); min-width 0 lets the input shrink with the field instead of running out of it and
     pushing the clear button off the edge. align-self stretch: the input fills the field's full height, so a tap
     anywhere on the 38 px field types into it (the input itself was a 21 px strip). Desktop unchanged. */
  @media (max-width: 768px) {
    min-width: 0;
    align-self: stretch;
  }
`;

const ClearButton = styled.button<{ $liquid?: boolean }>`
  border: none;
  background: transparent;
  color: ${(p) => p.theme.muted};
  font-size: 11px;
  padding: 4px 6px;
  border-radius: ${(p) => (p.$liquid ? '999px' : '4px')}; /* [LIQUID-GLASS 2026-11-02] round inside the capsule */
  display: flex;
  align-items: center;
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${(p) => (p.$liquid ? lgControlHover(p) : p.theme.pill)};
    color: ${(p) => p.theme.text};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 32 px square to tap (the icon was a 20 px target), reaching into the field's
     right padding so the input keeps its width. Desktop unchanged. */
  @media (max-width: 768px) {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    margin-right: -8px;
    justify-content: center;
  }
`;

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Render just the field (no full-width strip) so it can sit inside a toolbar row. */
  inline?: boolean;
  /** [LIQUID-GLASS 2026-11-02] The liquid glass capsule (styles/liquidGlass.ts); off by default. */
  liquid?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ value, onChange, placeholder = 'Search payments...', inline = false, liquid = false }) => {
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocalValue(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChange(val), 300);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  const field = (
    <Field $inline={inline} $liquid={liquid}>
      <Icon>
        <i className="fas fa-search"></i>
      </Icon>
      <Input
        type="text"
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
      />
      {localValue && (
        <ClearButton $liquid={liquid} onClick={handleClear}>
          <i className="fas fa-times"></i>
        </ClearButton>
      )}
    </Field>
  );

  // Inline mode skips the full-width strip so the field can live inside a toolbar.
  return inline ? field : <Container>{field}</Container>;
};

export default SearchBar;
